/* Related parties (asset finance) — the people behind the entity.
 *
 * An asset-finance application isn't about one borrower: it's about an
 * entity and the directors, partners, trustees and guarantors standing
 * behind it. Exactly one party is the PRIMARY CONTACT — that person's name,
 * email and mobile are what the platform emails, threads replies against and
 * shows in the queue, so the caller mirrors them to the top-level lead
 * fields (see syncPrimaryToLead in src/lib/parties.js).
 */
import { BRAND } from "../brand.js";
import React, { useState } from "react";
import { Icon } from "./Icon.jsx";
import { Field, TextField, Select, TileSelect, YesNo, TextArea, AddressField } from "./fields.jsx";
import { OPT } from "../data/data.js";
import { blankParty, partyName, partyComplete, partyIssues } from "../lib/parties.js";

const REL_LABEL = (v) => (OPT.partyRelationship.find((o) => o.v === v) || {}).l || v;

/* One party's full detail form. Long by nature — it's a credit file on a
   person — so it's grouped rather than one wall of inputs. */
function PartyForm({ party, onChange, onDone, onCancel, isOnly }) {
  const set = (patch) => onChange({ ...party, ...patch });
  const issues = partyIssues(party);
  return (
    <div className="pf-card pf-rise" style={{ padding: 18, display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div className="pf-grouphead">Their role</div>
        <Field label="Relationship to the business">
          <TileSelect value={party.relationship} onChange={(v) => set({ relationship: v })} cols={1} options={OPT.partyRelationship} />
        </Field>
        <Field label={`Are they an existing ${BRAND.name} customer?`}>
          <YesNo value={party.isExistingCustomer} onChange={(v) => set({ isExistingCustomer: v })} />
        </Field>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div className="pf-grouphead">Their details</div>
        <div style={{ display: "grid", gridTemplateColumns: "110px 1fr", gap: 12 }}>
          <Field label="Title"><Select value={party.title} onChange={(v) => set({ title: v })} options={OPT.title} placeholder="-" /></Field>
          <Field label="First name"><TextField value={party.firstName} autoCap onChange={(v) => set({ firstName: v })} placeholder="Dana" /></Field>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Middle name (optional)"><TextField value={party.middleName} autoCap onChange={(v) => set({ middleName: v })} /></Field>
          <Field label="Last name"><TextField value={party.lastName} autoCap onChange={(v) => set({ lastName: v })} placeholder="Whitlock" /></Field>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Date of birth"><TextField type="date" value={party.dateOfBirth} onChange={(v) => set({ dateOfBirth: v })} /></Field>
          <Field label="Gender"><Select value={party.gender} onChange={(v) => set({ gender: v })} options={OPT.gender} placeholder="-" /></Field>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Mobile number"><TextField type="tel" inputMode="numeric" value={party.mobile} onChange={(v) => set({ mobile: v })} placeholder="04XX XXX XXX" /></Field>
          <Field label="Home phone (optional)"><TextField type="tel" inputMode="numeric" value={party.homePhone} onChange={(v) => set({ homePhone: v })} placeholder="07 XXXX XXXX" /></Field>
        </div>
        <Field label="Email address"><TextField type="email" inputMode="email" value={party.email} onChange={(v) => set({ email: v })} placeholder="name@email.com" /></Field>
        <Field label="Residential address"><AddressField value={party.address} onChange={(v) => set({ address: v })} /></Field>
        <Field label="When did they move to this address?" hint="Approximate is fine.">
          <TextField type="month" value={party.movedIn} onChange={(v) => set({ movedIn: v })} />
        </Field>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div className="pf-grouphead">Credit background</div>
        <Field label="Any defaults, judgments or arrears in the last 5 years?">
          <YesNo value={party.creditHistoryIssues} onChange={(v) => set({ creditHistoryIssues: v, ...(v ? {} : { creditHistoryDetail: "" }) })} />
        </Field>
        {party.creditHistoryIssues === true && (
          <Field label="Tell us briefly what happened" hint="It doesn't rule anything out - we just need the picture.">
            <TextArea rows={2} value={party.creditHistoryDetail} onChange={(v) => set({ creditHistoryDetail: v })} />
          </Field>
        )}
        <Field label="Ever been bankrupt, or entered a Part IX or Part X arrangement?">
          <YesNo value={party.bankrupt} onChange={(v) => set({ bankrupt: v, ...(v ? {} : { bankruptDetail: "" }) })} />
        </Field>
        {party.bankrupt === true && (
          <Field label="When was it, and has it been discharged?">
            <TextArea rows={2} value={party.bankruptDetail} onChange={(v) => set({ bankruptDetail: v })} />
          </Field>
        )}
        <Field label="Any tax obligations in another country?" hint="Being a tax resident or citizen elsewhere, including the US.">
          <YesNo value={party.overseasTax} onChange={(v) => set({ overseasTax: v, ...(v ? {} : { overseasTaxDetail: "" }) })} />
        </Field>
        {party.overseasTax === true && (
          <Field label="Which country?">
            <TextField value={party.overseasTaxDetail} onChange={(v) => set({ overseasTaxDetail: v })} placeholder="e.g. New Zealand" />
          </Field>
        )}
      </div>

      {issues.length > 0 && (
        <div className="pf-hint" style={{ color: "var(--coral-600)" }}>Still needed: {issues.join(", ")}.</div>
      )}
      <div style={{ display: "flex", gap: 10 }}>
        {!isOnly && <button type="button" className="pf-btn pf-btn--ghost" style={{ flex: 1 }} onClick={onCancel}>Cancel</button>}
        <button type="button" className="pf-btn" style={{ flex: 1, height: 48 }} disabled={issues.length > 0} onClick={onDone}>
          {partyComplete(party) ? "Save this person" : "Save"}
        </button>
      </div>
    </div>
  );
}

export function RelatedParties({ parties, onChange }) {
  const [editing, setEditing] = useState(null);   // party id being edited
  const [draft, setDraft] = useState(null);

  const startAdd = () => { const p = blankParty(); setDraft(p); setEditing(p.id); };
  const startEdit = (p) => { setDraft({ ...p }); setEditing(p.id); };
  const commit = () => {
    const exists = parties.some((p) => p.id === draft.id);
    onChange(exists ? parties.map((p) => (p.id === draft.id ? draft : p)) : [...parties, draft]);
    setEditing(null); setDraft(null);
  };
  const remove = (id) => {
    const left = parties.filter((p) => p.id !== id);
    // The list must never be left without a primary contact.
    if (left.length && !left.some((p) => p.isPrimary)) left[0] = { ...left[0], isPrimary: true };
    onChange(left);
  };
  const makePrimary = (id) => onChange(parties.map((p) => ({ ...p, isPrimary: p.id === id })));

  return (
    <div>
      {parties.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
          {parties.map((p) => (
            <div key={p.id} className="pf-card" style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
              <span className="pf-tile__icon" style={{ width: 38, height: 38 }}><Icon name="user" size={19} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14.5, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  {partyName(p) || "Unnamed"}
                  {p.isPrimary && <span className="pf-badge">Main contact</span>}
                  {!partyComplete(p) && <span className="pf-badge pf-badge--warn">Incomplete</span>}
                </div>
                <div style={{ fontSize: 12.5, color: "var(--ink-45)" }}>
                  {REL_LABEL(p.relationship)}{p.email ? ` · ${p.email}` : ""}
                </div>
              </div>
              {!p.isPrimary && (
                <button type="button" className="pf-linkbtn" onClick={() => makePrimary(p.id)}>Make main contact</button>
              )}
              <button type="button" className="pf-back" style={{ width: 34, height: 34 }} aria-label={`Edit ${partyName(p)}`} onClick={() => startEdit(p)}>
                <Icon name="edit" size={16} />
              </button>
              {parties.length > 1 && (
                <button type="button" className="pf-back" style={{ width: 34, height: 34 }} aria-label={`Remove ${partyName(p)}`} onClick={() => remove(p.id)}>
                  <Icon name="close" size={17} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {editing ? (
        <PartyForm party={draft} onChange={setDraft} onDone={commit}
                   onCancel={() => { setEditing(null); setDraft(null); }}
                   isOnly={parties.length === 0} />
      ) : (
        <button type="button" className="pf-btn pf-btn--ghost" onClick={startAdd}>
          {parties.length ? "Add another person" : "Add the first person"}
        </button>
      )}
    </div>
  );
}
